import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  Breadcrumbs,
  Link,
  Grid,
  Skeleton,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Alert,
  MenuItem,
  Select,
  Tabs,
  Tab,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import TableChartIcon from '@mui/icons-material/TableChart';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ImageIcon from '@mui/icons-material/Image';
import ArchiveIcon from '@mui/icons-material/Archive';
import CodeIcon from '@mui/icons-material/Code';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { personsAPI, documentsAPI } from '../services/api';
import { toast } from 'react-toastify';
import PersonEditDialog from '../components/PersonEditDialog';
import PersonMergeDialog from '../components/PersonMerge/PersonMergeDialog';
import DocumentPreviewDialog from '../components/DocumentPreviewDialog';
import { usePermissions } from '../hooks/useAuth';
import PersonHeader from './Person/PersonHeader';
import PersonContactSection from './Person/PersonContactSection';
import PersonDirectorSection from './Person/PersonDirectorSection';
import PersonShareholderSection from './Person/PersonShareholderSection';

const Person: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [graph, setGraph] = useState<any | null>(null);
  const [personNode, setPersonNode] = useState<any | null>(null);
  const [person, setPerson] = useState<any | null>(null);
  const [directorships, setDirectorships] = useState<Array<{ companyId: string; companyName: string; roleTitle?: string; isNominee?: boolean }>>([]);
  const [editOpen, setEditOpen] = useState(false);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);

  // Tab and view state
  const [activeTab, setActiveTab] = useState(0); // 0: Overview, 1: Directorships, 2: Shareholdings, 3: Documents
  const [directorshipsView, setDirectorshipsView] = useState<'list' | 'graph'>('list');
  const [shareholdingsView, setShareholdingsView] = useState<'list' | 'graph'>('list');

  // Document management state
  const [documents, setDocuments] = useState<any[]>([]);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [uploadForm, setUploadForm] = useState<any>({ documentType: 'OTHER', tags: [] });
  const [isDragging, setIsDragging] = useState(false);
  const [editDocumentDialogOpen, setEditDocumentDialogOpen] = useState(false);
  const [editingDocument, setEditingDocument] = useState<any>(null);

  // Document preview state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewDocumentId, setPreviewDocumentId] = useState<string | null>(null);
  const [previewDocumentIndex, setPreviewDocumentIndex] = useState<number>(0);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        // Fetch person-specific data
        const [p, d, docs, networkGraph] = await Promise.all([
          id ? personsAPI.getById(String(id)) : Promise.resolve({ data: null }),
          id ? personsAPI.getDirectorships(String(id)) : Promise.resolve({ data: [] }),
          id ? documentsAPI.getByPerson(String(id)) : Promise.resolve({ data: [] }),
          id ? personsAPI.getNetworkGraph(String(id), 'directorships,shareholdings') : Promise.resolve({ data: null }),
        ]);

        setPerson(p?.data || null);
        const dList = Array.isArray(d?.data) ? d.data : [];
        setDirectorships(dList.map((x: any) => ({ companyId: x.companyId, companyName: x.companyName, roleTitle: x.roleTitle, isNominee: x.isNominee })));
        setDocuments(Array.isArray(docs?.data?.data) ? docs.data.data : (Array.isArray(docs?.data) ? docs.data : []));

        // Process network graph data
        if (networkGraph?.data) {
          const data = networkGraph.data;
          // Build graph structure compatible with existing code
          const nodes = [data.centerNode, ...data.companies];
          const edges = [...(data.directorships || []), ...(data.shareholdings || [])];

          setPersonNode(data.centerNode);
          setGraph({ nodes, edges });
        }
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  const shareholdings = useMemo(() => {
    if (!graph || !personNode) return [] as Array<{ company: any; edge: any }>;
    const outgoing = (graph.edges || []).filter((e: any) => e.source === `person-${id}`);
    const nodeById: Record<string, any> = {};
    (graph.nodes || []).forEach((n: any) => (nodeById[n.id] = n));
    return outgoing.map((e: any) => ({ company: nodeById[e.target], edge: e }));
  }, [graph, personNode, id]);

  const handleEditSave = async () => {
    setEditOpen(false);

    // Refresh person data
    if (!id) return;
    try {
      const p = await personsAPI.getById(String(id));
      setPerson(p.data);
    } catch (e: any) {
      console.error('Failed to refresh person data', e);
    }

    // If person was deleted, navigate away
    if (!person) {
      navigate('/people');
    }
  };

  const personTypes = useMemo(() => {
    return Array.isArray(person?.types) ? person.types : [];
  }, [person]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Document management helper functions
  const getFileTypeIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase() || '';
    switch (ext) {
      case 'pdf':
        return <PictureAsPdfIcon fontSize="small" sx={{ color: '#d32f2f' }} />;
      case 'doc':
      case 'docx':
        return <ArticleIcon fontSize="small" sx={{ color: '#2196f3' }} />;
      case 'xls':
      case 'xlsx':
      case 'csv':
        return <TableChartIcon fontSize="small" sx={{ color: '#4caf50' }} />;
      case 'ppt':
      case 'pptx':
        return <AssessmentIcon fontSize="small" sx={{ color: '#ff9800' }} />;
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
      case 'bmp':
        return <ImageIcon fontSize="small" sx={{ color: '#9c27b0' }} />;
      case 'zip':
      case 'rar':
      case '7z':
        return <ArchiveIcon fontSize="small" sx={{ color: '#795548' }} />;
      case 'txt':
      case 'js':
      case 'ts':
      case 'jsx':
      case 'tsx':
      case 'json':
      case 'xml':
        return <CodeIcon fontSize="small" sx={{ color: '#9e9e9e' }} />;
      default:
        return <InsertDriveFileIcon fontSize="small" sx={{ color: '#9e9e9e' }} />;
    }
  };

  const formatDocumentType = (type: string) => {
    return type
      .split('_')
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // Document handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    setUploadFiles((prev) => [...prev, ...files]);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setUploadFiles((prev) => [...prev, ...files]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setUploadFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUploadDocument = async () => {
    if (!id) return;
    let successCount = 0;
    let failCount = 0;

    for (const file of uploadFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('title', file.name);
        formData.append('documentType', uploadForm.documentType);
        formData.append('personId', String(id));

        // Optional metadata fields
        if (uploadForm.description) formData.append('description', uploadForm.description);
        if (uploadForm.documentDate) formData.append('documentDate', uploadForm.documentDate);
        if (uploadForm.expiryDate) formData.append('expiryDate', uploadForm.expiryDate);
        if (uploadForm.tags && uploadForm.tags.length > 0) formData.append('tags', JSON.stringify(uploadForm.tags));

        await documentsAPI.upload(formData);
        successCount++;
      } catch (e) {
        console.error('Failed to upload', file.name, e);
        failCount++;
      }
    }

    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} uploaded successfully`);
      // Reload documents
      const docsRes = await documentsAPI.getByPerson(String(id));
      setDocuments(Array.isArray(docsRes?.data?.data) ? docsRes.data.data : (Array.isArray(docsRes?.data) ? docsRes.data : []));
    }
    if (failCount > 0) {
      toast.error(`${failCount} document${failCount > 1 ? 's' : ''} failed to upload`);
    }

    setUploadDialogOpen(false);
    setUploadFiles([]);
    setUploadForm({ documentType: 'OTHER', tags: [], description: '', documentDate: '', expiryDate: '' });
  };

  const handleDownloadDocument = async (docId: string, filename: string) => {
    try {
      const response = await documentsAPI.download(docId);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded');
    } catch (e: any) {
      toast.error('Failed to download document');
      console.error(e);
    }
  };

  const handleDeleteDocument = async (docId: string) => {
    if (!window.confirm('Delete this document? This action cannot be undone.')) return;
    if (!id) return;
    try {
      await documentsAPI.delete(docId);
      toast.success('Document deleted');
      const docsRes = await documentsAPI.getByPerson(String(id));
      setDocuments(Array.isArray(docsRes?.data?.data) ? docsRes.data.data : (Array.isArray(docsRes?.data) ? docsRes.data : []));
    } catch (e: any) {
      toast.error('Failed to delete document');
      console.error(e);
    }
  };

  const handlePreviewDocument = (doc: any, index: number) => {
    // Store current document for metadata access
    setEditingDocument(doc);

    // Flatten all documents into a single array for navigation
    const allDocs: any[] = [];
    const grouped = documents.reduce((acc: any, d: any) => {
      const type = d.documentType || 'OTHER';
      if (!acc[type]) acc[type] = [];
      acc[type].push(d);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([type, docs]: [string, any]) => {
      allDocs.push(...docs);
    });

    const docIndex = allDocs.findIndex((d: any) => d.id === doc.id);

    setPreviewDocumentId(doc.id);
    setPreviewDocumentIndex(docIndex >= 0 ? docIndex : 0);
    setPreviewDialogOpen(true);
  };

  const handlePreviewNavigate = (index: number) => {
    const allDocs: any[] = [];
    const grouped = documents.reduce((acc: any, d: any) => {
      const type = d.documentType || 'OTHER';
      if (!acc[type]) acc[type] = [];
      acc[type].push(d);
      return acc;
    }, {});

    Object.entries(grouped).forEach(([type, docs]: [string, any]) => {
      allDocs.push(...docs);
    });

    if (index >= 0 && index < allDocs.length) {
      setPreviewDocumentId(allDocs[index].id);
      setPreviewDocumentIndex(index);
    }
  };

  const handleOpenEditDocument = (doc: any) => {
    setEditingDocument({
      id: doc.id,
      title: doc.title,
      description: doc.description || '',
      documentType: doc.documentType,
      tags: doc.tags || [],
      documentDate: doc.documentDate || '',
      expiryDate: doc.expiryDate || '',
    });
    setEditDocumentDialogOpen(true);
  };

  const handleSaveDocumentEdit = async () => {
    if (!id) return;
    try {
      await documentsAPI.update(editingDocument.id, {
        title: editingDocument.title,
        description: editingDocument.description,
        documentType: editingDocument.documentType,
        tags: editingDocument.tags,
        documentDate: editingDocument.documentDate,
        expiryDate: editingDocument.expiryDate,
      });
      toast.success('Document updated');
      const docsRes = await documentsAPI.getByPerson(String(id));
      setDocuments(Array.isArray(docsRes?.data?.data) ? docsRes.data.data : (Array.isArray(docsRes?.data) ? docsRes.data : []));
      setEditDocumentDialogOpen(false);
      setEditingDocument(null);
    } catch (e: any) {
      toast.error('Failed to update document');
      console.error(e);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Dashboard
        </Link>
        <Link component={RouterLink} to="/people" underline="hover" color="inherit">
          People
        </Link>
        <Typography color="text.primary">{person?.name || personNode?.label || 'Person'}</Typography>
      </Breadcrumbs>

      {loading ? (
        <Paper sx={{ p: 3 }}>
          <Skeleton variant="rectangular" height={120} />
        </Paper>
      ) : (person || personNode) ? (
        <>
          <PersonHeader
            name={person?.name || personNode?.label || 'Person'}
            types={personTypes}
            email={person?.email}
            phone={person?.phone}
            avatarUrl={person?.profilePhotoUrl && id ? personsAPI.getProfilePhotoUrl(id) : undefined}
            directorshipsCount={directorships.length}
            shareholdingsCount={shareholdings.length}
            onEdit={() => setEditOpen(true)}
            onMerge={() => setMergeDialogOpen(true)}
            hasPermission={hasPermission}
            getInitials={getInitials}
          />

          {/* Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab label="Overview" />
              <Tab label={`Directorships (${directorships.length})`} />
              <Tab label={`Shareholdings (${shareholdings.length})`} />
              <Tab label={`Documents (${documents.length})`} />
            </Tabs>
          </Paper>

          {/* Tab Panel 0: Overview */}
          {activeTab === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <PersonContactSection person={person} />
              </Grid>
              <Grid item xs={12} md={4}>
                <PersonDirectorSection
                  directorships={directorships}
                  view="list"
                  onViewChange={setDirectorshipsView}
                  personId={id}
                  showViewToggle={false}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <PersonShareholderSection
                  shareholdings={shareholdings}
                  view="list"
                  onViewChange={setShareholdingsView}
                  personId={id}
                  showViewToggle={false}
                />
              </Grid>

            {/* Personal Information */}
            {(person?.dateOfBirth || person?.placeOfBirth || person?.gender || person?.maritalStatus || person?.occupation) && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PersonIcon color="primary" />
                      Personal Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1.5}>
                      {person?.dateOfBirth && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Date of Birth
                          </Typography>
                          <Typography variant="body2">
                            {new Date(person.dateOfBirth).toLocaleDateString()}
                          </Typography>
                        </Box>
                      )}
                      {person?.placeOfBirth && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Place of Birth
                          </Typography>
                          <Typography variant="body2">{person.placeOfBirth}</Typography>
                        </Box>
                      )}
                      {person?.gender && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Gender
                          </Typography>
                          <Typography variant="body2">
                            {person.gender.replace(/_/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                          </Typography>
                        </Box>
                      )}
                      {person?.maritalStatus && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Marital Status
                          </Typography>
                          <Typography variant="body2">
                            {person.maritalStatus.charAt(0) + person.maritalStatus.slice(1).toLowerCase()}
                          </Typography>
                        </Box>
                      )}
                      {person?.occupation && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Occupation
                          </Typography>
                          <Typography variant="body2">{person.occupation}</Typography>
                        </Box>
                      )}
                      {person?.currentResidence && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Current Residence
                          </Typography>
                          <Typography variant="body2">{person.currentResidence}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Identification & Tax */}
            {(person?.passportNumber || person?.passportExpiryDate || person?.taxResidency || person?.taxIdentificationNumber) && (
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <AccountBalanceIcon color="primary" />
                      Identification & Tax
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                    <Stack spacing={1.5}>
                      {person?.passportNumber && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Passport Number
                          </Typography>
                          <Typography variant="body2">{person.passportNumber}</Typography>
                        </Box>
                      )}
                      {person?.passportExpiryDate && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Passport Expiry Date
                          </Typography>
                          <Typography variant="body2">
                            {new Date(person.passportExpiryDate).toLocaleDateString()}
                            {new Date(person.passportExpiryDate) < new Date() && (
                              <Chip
                                label="Expired"
                                size="small"
                                color="error"
                                sx={{ ml: 1, height: '18px' }}
                              />
                            )}
                          </Typography>
                        </Box>
                      )}
                      {person?.taxResidency && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Tax Residency
                          </Typography>
                          <Typography variant="body2">{person.taxResidency}</Typography>
                        </Box>
                      )}
                      {person?.taxIdentificationNumber && (
                        <Box>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Tax Identification Number
                          </Typography>
                          <Typography variant="body2">{person.taxIdentificationNumber}</Typography>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Documents */}
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <DescriptionIcon color="primary" />
                      <Typography variant="h6">
                        Documents
                      </Typography>
                      <Chip label={documents.length} size="small" color="primary" />
                    </Box>
                    {hasPermission('documents:create') && (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<UploadFileIcon />}
                        onClick={() => setUploadDialogOpen(true)}
                      >
                        Upload
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  {documents.length === 0 ? (
                    <Alert severity="info">
                      No documents uploaded yet.
                    </Alert>
                  ) : (
                    (() => {
                      // Group documents by type
                      const grouped = documents.reduce((acc: any, doc: any) => {
                        const type = doc.documentType || 'OTHER';
                        if (!acc[type]) acc[type] = [];
                        acc[type].push(doc);
                        return acc;
                      }, {});

                      return (
                        <Box>
                          {Object.entries(grouped).map(([type, docs]: [string, any]) => (
                            <Box key={type} mb={3}>
                              <Box display="flex" alignItems="center" gap={1} mb={1}>
                                <FolderIcon fontSize="small" color="primary" />
                                <Typography variant="subtitle2" fontWeight="bold">
                                  {formatDocumentType(type)}
                                </Typography>
                                <Chip label={docs.length} size="small" />
                              </Box>
                              <List dense>
                                {docs.map((doc: any) => (
                                  <Box key={doc.id} display="flex" alignItems="center" py={1} pl={4}>
                                    {getFileTypeIcon(doc.filename)}
                                    <Box flex={1} ml={1}>
                                      <Typography variant="body2" fontWeight="medium">
                                        {doc.title}
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" display="block">
                                        {doc.filename} • {formatFileSize(doc.fileSize || 0)}
                                      </Typography>
                                      {doc.description && (
                                        <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                                          {doc.description}
                                        </Typography>
                                      )}
                                      <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                                        <Chip
                                          label={`Uploaded: ${new Date(doc.createdAt).toLocaleDateString()}`}
                                          size="small"
                                          variant="outlined"
                                          sx={{ height: '18px', fontSize: '0.7rem' }}
                                        />
                                        {doc.documentDate && (
                                          <Chip
                                            label={`Doc Date: ${new Date(doc.documentDate).toLocaleDateString()}`}
                                            size="small"
                                            variant="outlined"
                                            color="primary"
                                            sx={{ height: '18px', fontSize: '0.7rem' }}
                                          />
                                        )}
                                        {doc.expiryDate && (
                                          <Chip
                                            label={`Expires: ${new Date(doc.expiryDate).toLocaleDateString()}`}
                                            size="small"
                                            variant="outlined"
                                            color="warning"
                                            sx={{ height: '18px', fontSize: '0.7rem' }}
                                          />
                                        )}
                                      </Box>
                                    </Box>
                                    <Tooltip title="Preview">
                                      <IconButton
                                        aria-label="Preview document"
                                        size="small"
                                        color="primary"
                                        onClick={() => handlePreviewDocument(doc, docs.indexOf(doc))}
                                      >
                                        <VisibilityIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Edit">
                                      <IconButton
                                        aria-label="Edit document"
                                        size="small"
                                        color="default"
                                        onClick={() => handleOpenEditDocument(doc)}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Download">
                                      <IconButton
                                        aria-label="Download document"
                                        size="small"
                                        color="success"
                                        onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                                      >
                                        <DownloadIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                    {hasPermission('documents:delete') && (
                                      <Tooltip title="Delete">
                                        <IconButton
                                          aria-label="Delete document"
                                          size="small"
                                          color="error"
                                          onClick={() => handleDeleteDocument(doc.id)}
                                        >
                                          <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                  </Box>
                                ))}
                              </List>
                            </Box>
                          ))}
                        </Box>
                      );
                    })()
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
          )}

          {/* Tab Panel 1: Directorships */}
          {activeTab === 1 && (
            <PersonDirectorSection
              directorships={directorships}
              view={directorshipsView}
              onViewChange={setDirectorshipsView}
              personId={id}
              showViewToggle
            />
          )}

          {/* Tab Panel 2: Shareholdings */}
          {activeTab === 2 && (
            <PersonShareholderSection
              shareholdings={shareholdings}
              view={shareholdingsView}
              onViewChange={setShareholdingsView}
              personId={id}
              showViewToggle
            />
          )}

          {/* Tab Panel 3: Documents */}
          {activeTab === 3 && (
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box display="flex" alignItems="center" gap={1}>
                    <DescriptionIcon color="primary" />
                    <Typography variant="h6">
                      Documents
                    </Typography>
                    <Chip label={documents.length} size="small" color="primary" />
                  </Box>
                  {hasPermission('documents:create') && (
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<UploadFileIcon />}
                      onClick={() => setUploadDialogOpen(true)}
                    >
                      Upload
                    </Button>
                  )}
                </Box>
                <Divider sx={{ mb: 2 }} />
                {documents.length === 0 ? (
                  <Alert severity="info">
                    No documents uploaded yet.
                  </Alert>
                ) : (
                  (() => {
                    const grouped = documents.reduce((acc: any, doc: any) => {
                      const type = doc.documentType || 'OTHER';
                      if (!acc[type]) acc[type] = [];
                      acc[type].push(doc);
                      return acc;
                    }, {});

                    return (
                      <Box>
                        {Object.entries(grouped).map(([type, docs]: [string, any]) => (
                          <Box key={type} mb={3}>
                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                              <FolderIcon fontSize="small" color="primary" />
                              <Typography variant="subtitle2" fontWeight="bold">
                                {formatDocumentType(type)}
                              </Typography>
                              <Chip label={docs.length} size="small" />
                            </Box>
                            <List dense>
                              {docs.map((doc: any) => (
                                <Box key={doc.id} display="flex" alignItems="center" py={1} pl={4}>
                                  {getFileTypeIcon(doc.filename)}
                                  <Box flex={1} ml={1}>
                                    <Typography variant="body2" fontWeight="medium">
                                      {doc.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                      {doc.filename} • {formatFileSize(doc.fileSize || 0)}
                                    </Typography>
                                    {doc.description && (
                                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic' }}>
                                        {doc.description}
                                      </Typography>
                                    )}
                                    <Box display="flex" gap={1} mt={0.5} flexWrap="wrap">
                                      <Chip
                                        label={`Uploaded: ${new Date(doc.createdAt).toLocaleDateString()}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: '18px', fontSize: '0.7rem' }}
                                      />
                                      {doc.documentDate && (
                                        <Chip
                                          label={`Doc Date: ${new Date(doc.documentDate).toLocaleDateString()}`}
                                          size="small"
                                          variant="outlined"
                                          color="primary"
                                          sx={{ height: '18px', fontSize: '0.7rem' }}
                                        />
                                      )}
                                      {doc.expiryDate && (
                                        <Chip
                                          label={`Expires: ${new Date(doc.expiryDate).toLocaleDateString()}`}
                                          size="small"
                                          variant="outlined"
                                          color="warning"
                                          sx={{ height: '18px', fontSize: '0.7rem' }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                  <Tooltip title="Preview">
                                    <IconButton
                                      aria-label="Preview document"
                                      size="small"
                                      color="primary"
                                      onClick={() => handlePreviewDocument(doc, docs.indexOf(doc))}
                                    >
                                      <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Edit">
                                    <IconButton
                                      aria-label="Edit document"
                                      size="small"
                                      color="default"
                                      onClick={() => handleOpenEditDocument(doc)}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Download">
                                    <IconButton
                                      aria-label="Download document"
                                      size="small"
                                      color="success"
                                      onClick={() => handleDownloadDocument(doc.id, doc.filename)}
                                    >
                                      <DownloadIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                  {hasPermission('documents:delete') && (
                                    <Tooltip title="Delete">
                                      <IconButton
                                        aria-label="Delete document"
                                        size="small"
                                        color="error"
                                        onClick={() => handleDeleteDocument(doc.id)}
                                      >
                                        <DeleteOutlineIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                              ))}
                            </List>
                          </Box>
                        ))}
                      </Box>
                    );
                  })()
                )}
              </CardContent>
            </Card>
          )}
        </>
      ) : (
        <Paper sx={{ p: 3 }}>
          <Alert severity="warning">Person not found.</Alert>
        </Paper>
      )}

      {/* Edit Dialog */}
      <PersonEditDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        person={person}
        onSave={handleEditSave}
        showAdvancedFields={true}
      />

      {/* Upload Document Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Upload Documents</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input-person')?.click()}
              sx={{
                border: '2px dashed',
                borderColor: isDragging ? 'primary.main' : 'divider',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                bgcolor: isDragging ? 'action.hover' : 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'action.hover',
                },
              }}
            >
              <UploadFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
              <Typography variant="body1" gutterBottom>
                {isDragging ? 'Drop files here' : 'Drag & drop files here or click to browse'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Supports multiple files
              </Typography>
              <input
                id="file-input-person"
                type="file"
                multiple
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
            </Box>

            {uploadFiles.length > 0 && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Selected Files ({uploadFiles.length})
                </Typography>
                <List dense>
                  {uploadFiles.map((file, index) => (
                    <ListItem
                      key={index}
                      secondaryAction={
                        <IconButton aria-label="Remove file" edge="end" size="small" onClick={() => handleRemoveFile(index)}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024).toFixed(1)} KB`}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Document Type
              </Typography>
              <Select
                value={uploadForm.documentType}
                onChange={(e) => setUploadForm({ ...uploadForm, documentType: e.target.value })}
                fullWidth
                size="small"
              >
                <MenuItem value="INCORPORATION">Incorporation</MenuItem>
                <MenuItem value="SHAREHOLDER_REGISTER">Shareholder Register</MenuItem>
                <MenuItem value="BOARD_RESOLUTION">Board Resolution</MenuItem>
                <MenuItem value="FILING">Filing</MenuItem>
                <MenuItem value="FINANCIAL_STATEMENT">Financial Statement</MenuItem>
                <MenuItem value="CONTRACT">Contract</MenuItem>
                <MenuItem value="CERTIFICATE">Certificate</MenuItem>
                <MenuItem value="CORRESPONDENCE">Correspondence</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </Box>

            {/* Description */}
            <TextField
              label="Description (Optional)"
              value={uploadForm.description || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
              placeholder="Add a description for these documents"
            />

            {/* Document Date */}
            <TextField
              label="Document Date (Optional)"
              type="date"
              value={uploadForm.documentDate || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, documentDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="The date on the document itself"
            />

            {/* Expiry Date */}
            <TextField
              label="Expiry Date (Optional)"
              type="date"
              value={uploadForm.expiryDate || ''}
              onChange={(e) => setUploadForm({ ...uploadForm, expiryDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="When this document expires or needs renewal"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setUploadDialogOpen(false);
            setUploadFiles([]);
            setUploadForm({ documentType: 'OTHER', tags: [], description: '', documentDate: '', expiryDate: '' });
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadDocument}
            disabled={uploadFiles.length === 0}
            startIcon={<UploadFileIcon />}
          >
            Upload {uploadFiles.length > 0 && `(${uploadFiles.length})`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Document Dialog */}
      <Dialog open={editDocumentDialogOpen} onClose={() => setEditDocumentDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Document</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Title"
              value={editingDocument?.title || ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, title: e.target.value })}
              fullWidth
            />
            <TextField
              label="Description"
              value={editingDocument?.description || ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, description: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Document Type
              </Typography>
              <Select
                value={editingDocument?.documentType || 'OTHER'}
                onChange={(e) => setEditingDocument({ ...editingDocument, documentType: e.target.value })}
                fullWidth
                size="small"
              >
                <MenuItem value="INCORPORATION">Incorporation</MenuItem>
                <MenuItem value="SHAREHOLDER_REGISTER">Shareholder Register</MenuItem>
                <MenuItem value="BOARD_RESOLUTION">Board Resolution</MenuItem>
                <MenuItem value="FILING">Filing</MenuItem>
                <MenuItem value="FINANCIAL_STATEMENT">Financial Statement</MenuItem>
                <MenuItem value="CONTRACT">Contract</MenuItem>
                <MenuItem value="CERTIFICATE">Certificate</MenuItem>
                <MenuItem value="CORRESPONDENCE">Correspondence</MenuItem>
                <MenuItem value="OTHER">Other</MenuItem>
              </Select>
            </Box>

            {/* Document Date */}
            <TextField
              label="Document Date (Optional)"
              type="date"
              value={editingDocument?.documentDate ? new Date(editingDocument.documentDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, documentDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="The date on the document itself"
            />

            {/* Expiry Date */}
            <TextField
              label="Expiry Date (Optional)"
              type="date"
              value={editingDocument?.expiryDate ? new Date(editingDocument.expiryDate).toISOString().split('T')[0] : ''}
              onChange={(e) => setEditingDocument({ ...editingDocument, expiryDate: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              helperText="When this document expires or needs renewal"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setEditDocumentDialogOpen(false);
            setEditingDocument(null);
          }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveDocumentEdit}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Document Preview Dialog */}
      <DocumentPreviewDialog
        open={previewDialogOpen}
        onClose={() => {
          setPreviewDialogOpen(false);
          setPreviewDocumentId(null);
          setEditingDocument(null);
        }}
        documentId={previewDocumentId}
        documentFilename={editingDocument?.filename}
        documentTitle={editingDocument?.title || editingDocument?.description}
        documents={documents}
        currentIndex={previewDocumentIndex}
        onNavigate={handlePreviewNavigate}
      />

      {/* Merge Dialog */}
      <PersonMergeDialog
        open={mergeDialogOpen}
        onClose={() => setMergeDialogOpen(false)}
        targetPerson={person || undefined}
        onComplete={async () => {
          // Reload person data after merge
          if (id) {
            try {
              const p = await personsAPI.getById(String(id));
              setPerson(p.data);
            } catch (e) {
              console.error('Failed to refresh person data', e);
            }
          }
        }}
      />
    </Container>
  );
};

export default Person;
